/**
 * 按行求值 OperateAction.visibleExpression。
 *
 * 设计目标：
 * - 让设计器用户能直接书写 `status === 'active'` 这种短表达式（无需 record. 前缀）。
 * - 实现上用非严格 `new Function` + `with(row)`，把当前行的字段暴露为变量。
 * - 任何解析或运行异常一律视为不显示，避免单行错误影响整张表。
 *
 * 安全模型：表达式来源是页面 JSON（与业务同源、由可信设计器维护）。
 * 若未来出现「不可信第三方编辑 JSON」的场景，应替换为受限 DSL（如 jexl）。
 */
const MAX_EXPR_LEN = 256;
const FORBIDDEN_PATTERNS = [/__proto__/, /constructor\s*[.[(]/, /globalThis/];

const exprCache = new Map<string, (row: any) => unknown>();

const compileExpression = (expr: string): ((row: any) => unknown) | null => {
  const cached = exprCache.get(expr);
  if (cached) return cached;
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('row', `with(row){ return (${expr}); }`) as (row: any) => unknown;
    exprCache.set(expr, fn);
    return fn;
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.warn('[AndTable] visibleExpression compile failed:', expr, err);
    }
    return null;
  }
};

export const evaluateOperateVisible = (row: any, expression?: string | null): boolean => {
  if (expression == null) return true;
  const expr = String(expression).trim();
  if (!expr) return true;
  if (expr.length > MAX_EXPR_LEN) return false;
  if (FORBIDDEN_PATTERNS.some((re) => re.test(expr))) return false;
  const fn = compileExpression(expr);
  if (!fn) return false;
  try {
    return Boolean(fn(row && typeof row === 'object' ? row : {}));
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.warn('[AndTable] visibleExpression eval failed:', expr, err);
    }
    return false;
  }
};
